import { useEffect, useState } from "react";

import { Button, DrawerFooter, Heading, Span } from "@icg360/design-system";

import { MSSDrawer, MSSDrawerHeader } from "components/mss-drawer";
import drawerStyles from "components/mss-drawer/mss-drawer.module.scss";
import { useNotificationContext } from "components/notifications/context";
import { NotificationItem } from "components/notifications/notifications-drawer/notification-item";
import styles from "components/notifications/notifications-drawer/notifications-drawer.module.scss";
import CheckIcon from "images/icon-check.svg?react";

type NotificationDrawerProps = {
  isOpen: boolean;
  onClose: () => void;
};

type DrawerView = "notificationsPresent" | "noNotifications";

export const NotificationsDrawer = ({
  isOpen,
  onClose,
}: NotificationDrawerProps) => {
  const { notifications, total, dismissAllNotifications } =
    useNotificationContext();
  const [view, setView] = useState<DrawerView>("notificationsPresent");

  // Reset drawer state when opening
  useEffect(() => {
    if (isOpen) {
      setView(total === 0 ? "noNotifications" : "notificationsPresent");
    }
  }, [isOpen, total]);

  const drawerTitle = "Notifications";

  return (
    <MSSDrawer isOpen={isOpen} onClose={onClose}>
      <div className={styles.mssHeaderSection}>
        <MSSDrawerHeader
          title={drawerTitle}
          onClose={onClose}
          closeButtonTestId="notifications-drawer-switcher-close"
        />
      </div>
      {view === "notificationsPresent" ? (
        <>
          <MSSDrawer.Body className={styles.mssBody}>
            <div className={styles.drawerContent}>
              {notifications && (
                <NotificationItem
                  notifications={notifications}
                  onClose={onClose}
                />
              )}
            </div>
          </MSSDrawer.Body>
          <DrawerFooter className={drawerStyles.drawerFooterPadding}>
            <div className={drawerStyles.footerActions}>
              <Button
                appearance="tertiary"
                className={drawerStyles.footerBtn}
                onPress={dismissAllNotifications}
                data-testid="clear-all-button"
              >
                Clear All
              </Button>
            </div>
          </DrawerFooter>
        </>
      ) : (
        <>
          <MSSDrawer.Body className={styles.emptyMssBody}>
            <div className={styles.emptyDrawerContent}>
              <div className={styles.notificationEmptyImage}>
                <CheckIcon className={styles.checkImage} />
              </div>
              <Heading
                size="md"
                tag="h3"
                className={styles.notificationsEmptyState}
              >
                You're all caught up!
              </Heading>
              <Span size="sm" className={styles.emptyNotificationsDescription}>
                We'll let you know when something needs your attention.
              </Span>
            </div>
          </MSSDrawer.Body>
          <DrawerFooter className={drawerStyles.drawerFooterPadding}>
            <div className={drawerStyles.footerActions}>
              <Button
                appearance="tertiary"
                className={drawerStyles.footerBtn}
                disabled
                onPress={dismissAllNotifications}
                data-testid="clear-all-button"
              >
                Clear All
              </Button>
            </div>
          </DrawerFooter>
        </>
      )}
    </MSSDrawer>
  );
};
